import React, { FC, useMemo } from "react";
import DeprecatedIcon from "@src/deprecatedDesignSystem/components/DeprecatedIcon";
import Spinner from "@src/deprecatedDesignSystem/components/Spinner";
import { deprecatedTones } from "@src/deprecatedDesignSystem/styles/deprecatedColors";
import { cn } from "@src/ui/lib/utils";
import {
  ChatAttachmentLibraryItemFragment,
  ChatAttachmentMediaFragment,
} from "@src/gql/aiChat.generated";
import { ChatThumbnail } from "./ChatThumbnail";
import { LibraryItemHoverCard } from "../ChatAttachMenu/ChatAttachMenuContent";
import { Tooltip, TooltipTrigger, TooltipContent } from "@src/ui/tooltip";
import { FileUpload } from "@src/hooks/useUploadFileAndCreateSearchDoc";

export type ChatAttachmentState = "uploading" | "loading" | "loaded";

export interface ChatAttachmentItem {
  libraryItemFragment?: ChatAttachmentLibraryItemFragment | null;
  mediaFragment?: ChatAttachmentMediaFragment | null;
  url?: string | null;
  file?: File;
  uploadingFile?: FileUpload;
}

export interface ChatAttachmentProps extends ChatAttachmentItem {
  onRemove?: () => void;
  className?: string;
}

const ChatAttachment: FC<ChatAttachmentProps> = ({
  libraryItemFragment,
  mediaFragment,
  url,
  file,
  uploadingFile,
  onRemove,
  className,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const { name, type } = useMemo(() => {
    // Uploading file type
    if (file) {
      return {
        name: file.name,
        type: "file" as const,
      };
    }

    // LibraryItem type
    if (libraryItemFragment) {
      return {
        name: libraryItemFragment.name.en,
        type: "library" as const,
      };
    }

    // Media type
    if (mediaFragment) {
      return {
        name: decodeURIComponent(
          mediaFragment.mediaUrls.en.split("/").pop() || "",
        ),
        type: "media" as const,
      };
    }

    // URL type
    if (url) {
      // Extract domain from URL for display
      let displayName = url;
      try {
        const urlObj = new URL(url);
        displayName = urlObj.hostname;
      } catch {
        // If URL parsing fails, use the full URL
      }

      return {
        name: displayName,
        type: "url" as const,
      };
    }

    if (uploadingFile) {
      return {
        name: uploadingFile.fileName,
        type: "file",
      };
    }

    // Fallback
    return {
      name: "Unknown",
      type: "unknown" as const,
    };
  }, [libraryItemFragment, mediaFragment, url, file, uploadingFile]);
  const attachmentContent = (
    <div
      className={cn(
        "bg-white inline-flex items-center gap-1.5 px-1 py-1 pr-1.5 max-w-[200px] cursor-default transition-all duration-150 ease-in-out border border-gray-2 rounded-md hover:bg-gray-1 hover:border-gray-3",
        className,
        uploadingFile?.state === "error" && "bg-red-1 border-red-5",
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-center justify-center w-6 h-6 flex-shrink-0">
        {isHovered && onRemove ? (
          <button
            className="flex items-center justify-center w-6 h-6 rounded bg-gray-100 hover:bg-gray-200 border-none cursor-pointer transition-colors duration-150 ease-in-out"
            onClick={onRemove}
            aria-label="Remove attachment"
          >
            <DeprecatedIcon
              type="close"
              iconSize={20}
              color={deprecatedTones.gray10}
            />
          </button>
        ) : uploadingFile?.state === "uploading" ? (
          <div className="flex items-center justify-center w-6 h-6 rounded bg-gray-2">
            <Spinner size={16} />
          </div>
        ) : (
          <ChatThumbnail
            libraryItemFragment={libraryItemFragment}
            mediaFragment={mediaFragment}
            url={url}
            file={file}
            size={24}
          />
        )}
      </div>
      <span
        className={cn(
          "text-sm font-regular whitespace-nowrap overflow-hidden text-ellipsis min-w-0",
          "text-gray-900",
        )}
        title={name}
      >
        {name}
      </span>
    </div>
  );

  if (uploadingFile) {
    // Show tooltip for uploading or error
    const tooltipText =
      uploadingFile.state === "uploading"
        ? "File upload in progress"
        : uploadingFile.state === "error"
          ? uploadingFile.error || "Upload failed"
          : name;
    return (
      <Tooltip>
        <TooltipTrigger asChild>{attachmentContent}</TooltipTrigger>
        <TooltipContent>{tooltipText}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <LibraryItemHoverCard
      libraryItem={libraryItemFragment}
      hoverCardContentProps={{
        side: "top",
      }}
    >
      {attachmentContent}
    </LibraryItemHoverCard>
  );
};

export default ChatAttachment;
